import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';

/**
 * LocationContext — robust, multi-step geolocation.
 *
 * Strategy:
 * 1. Read 24h localStorage cache → if fresh, use immediately.
 * 2. On allowed routes only (/, /events, /categories), after a ~4s delay:
 *    a. Query Permissions API:
 *       - granted  → fetch silently
 *       - prompt   → expose `shouldShowPrompt = true` so UI banner can ask
 *       - denied   → skip browser, fall back to IP geolocation
 *    b. If browser geolocation fails or times out → IP fallback.
 *    c. If everything fails → expose `needsManualSelection = true`.
 * 3. Manual override via `setManualCity` (also cached).
 *
 * Critical: rendering NEVER blocks on this. `loading` is informational only.
 */

interface LocationState {
  city: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  loading: boolean;
  error: string | null;
  source: 'cache' | 'browser' | 'ip' | 'manual' | null;
}

type PermissionStatus = 'granted' | 'prompt' | 'denied' | 'unknown';

interface LocationContextType extends LocationState {
  requestLocation: () => void;
  setManualCity: (city: string, country?: string) => void;
  dismissPrompt: () => void;
  hasDetectedCity: boolean;
  shouldShowPrompt: boolean;
  needsManualSelection: boolean;
  permissionState: PermissionStatus;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

const CACHE_KEY = 'user_location';
const PROMPT_DISMISSED_KEY = 'user_location_prompt_dismissed';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours
const AUTO_REQUEST_DELAY = 4000; // 4 seconds
const GEOLOCATION_TIMEOUT = 8000;

const ALLOWED_ROUTE_PREFIXES = ['/events', '/categories'];
const isAllowedRoute = (pathname: string) => {
  if (pathname === '/') return true;
  return ALLOWED_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
};

const reverseGeocode = async (lat: number, lon: number) => {
  try {
    const res = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`,
    );
    if (res.ok) {
      const data = await res.json();
      return {
        city: data.city || data.locality || '',
        country: data.countryName || '',
      };
    }
  } catch (e) {
    console.warn('[Location] Reverse geocoding failed:', e);
  }
  return { city: '', country: '' };
};

const ipGeolocate = async (): Promise<Partial<LocationState> | null> => {
  // Two providers tried in order; both are CORS-friendly and free for low volume.
  const providers = [
    async () => {
      const res = await fetch('https://ipapi.co/json/');
      if (!res.ok) return null;
      const d = await res.json();
      if (!d.city) return null;
      return {
        city: d.city,
        country: d.country_name || '',
        latitude: typeof d.latitude === 'number' ? d.latitude : null,
        longitude: typeof d.longitude === 'number' ? d.longitude : null,
      };
    },
    async () => {
      const res = await fetch('https://get.geojs.io/v1/ip/geo.json');
      if (!res.ok) return null;
      const d = await res.json();
      if (!d.city) return null;
      return {
        city: d.city,
        country: d.country || '',
        latitude: d.latitude ? parseFloat(d.latitude) : null,
        longitude: d.longitude ? parseFloat(d.longitude) : null,
      };
    },
  ];

  for (const fn of providers) {
    try {
      const result = await fn();
      if (result) return result;
    } catch (e) {
      // try next
    }
  }
  return null;
};

const initialState: LocationState = {
  city: '',
  country: '',
  latitude: null,
  longitude: null,
  loading: false,
  error: null,
  source: null,
};

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<LocationState>(initialState);
  const [permissionState, setPermissionState] = useState<PermissionStatus>('unknown');
  const [shouldShowPrompt, setShouldShowPrompt] = useState(false);
  const [needsManualSelection, setNeedsManualSelection] = useState(false);

  const requestInFlightRef = useRef(false);
  const autoRequestTimerRef = useRef<number | null>(null);
  const hasInitializedRef = useRef(false);

  const persistLocation = useCallback((next: LocationState) => {
    try {
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: {
            city: next.city,
            country: next.country,
            lat: next.latitude,
            lng: next.longitude,
            source: next.source,
          },
          timestamp: Date.now(),
        }),
      );
    } catch {
      /* quota exceeded — non-critical */
    }
  }, []);

  const applyState = useCallback(
    (next: Partial<LocationState>) => {
      setState((prev) => ({ ...prev, ...next }));
    },
    [],
  );

  const fetchBrowserLocation = useCallback((): Promise<LocationState | null> => {
    return new Promise((resolve) => {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const { city, country } = await reverseGeocode(latitude, longitude);
          resolve({
            city,
            country,
            latitude,
            longitude,
            loading: false,
            error: null,
            source: 'browser',
          });
        },
        () => resolve(null),
        {
          enableHighAccuracy: false,
          timeout: GEOLOCATION_TIMEOUT,
          maximumAge: 5 * 60 * 1000,
        },
      );
    });
  }, []);

  const runFallbackChain = useCallback(
    async (opts: { tryBrowser: boolean }) => {
      if (requestInFlightRef.current) return;
      requestInFlightRef.current = true;
      applyState({ loading: true, error: null });

      try {
        // Step 1: Browser geolocation
        if (opts.tryBrowser) {
          const browserResult = await fetchBrowserLocation();
          if (browserResult && browserResult.city) {
            applyState(browserResult);
            persistLocation(browserResult);
            setShouldShowPrompt(false);
            setNeedsManualSelection(false);
            return;
          }
        }

        // Step 2: IP-based fallback
        const ipResult = await ipGeolocate();
        if (ipResult && ipResult.city) {
          const next: LocationState = {
            city: ipResult.city || '',
            country: ipResult.country || '',
            latitude: ipResult.latitude ?? null,
            longitude: ipResult.longitude ?? null,
            loading: false,
            error: null,
            source: 'ip',
          };
          applyState(next);
          persistLocation(next);
          setShouldShowPrompt(false);
          setNeedsManualSelection(false);
          return;
        }

        // Step 3: Manual selection required
        applyState({ loading: false, error: null });
        setNeedsManualSelection(true);
      } finally {
        requestInFlightRef.current = false;
      }
    },
    [applyState, fetchBrowserLocation, persistLocation],
  );

  const requestLocation = useCallback(() => {
    setShouldShowPrompt(false);
    void runFallbackChain({ tryBrowser: true });
  }, [runFallbackChain]);

  const setManualCity = useCallback(
    (city: string, country = '') => {
      const next: LocationState = {
        city,
        country,
        latitude: null,
        longitude: null,
        loading: false,
        error: null,
        source: 'manual',
      };
      applyState(next);
      persistLocation(next);
      setShouldShowPrompt(false);
      setNeedsManualSelection(false);
    },
    [applyState, persistLocation],
  );

  const dismissPrompt = useCallback(() => {
    setShouldShowPrompt(false);
    try {
      sessionStorage.setItem(PROMPT_DISMISSED_KEY, '1');
    } catch {
      /* ignore */
    }
    // Still try IP so the page is useful
    void runFallbackChain({ tryBrowser: false });
  }, [runFallbackChain]);

  // Initialize on mount + re-evaluate on route changes
  useEffect(() => {
    // 1. Hydrate from 24h cache (one-time)
    if (!hasInitializedRef.current) {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          const fresh = Date.now() - parsed.timestamp < CACHE_EXPIRY;
          if (fresh && parsed.data?.city) {
            applyState({
              city: parsed.data.city,
              country: parsed.data.country || '',
              latitude: parsed.data.lat ?? null,
              longitude: parsed.data.lng ?? null,
              loading: false,
              error: null,
              source: 'cache',
            });
            hasInitializedRef.current = true;
            return; // cache wins; nothing else to do
          }
          if (!fresh) localStorage.removeItem(CACHE_KEY);
        }
      } catch {
        try {
          localStorage.removeItem(CACHE_KEY);
        } catch {
          /* ignore */
        }
      }
    }

    const promptDismissed = (() => {
      try {
        return sessionStorage.getItem(PROMPT_DISMISSED_KEY) === '1';
      } catch {
        return false;
      }
    })();

    const scheduleIfAllowed = () => {
      if (typeof window === 'undefined' || !isAllowedRoute(window.location.pathname)) return;
      if (hasInitializedRef.current) return; // already detected or in progress
      hasInitializedRef.current = true;

      const inspect = async () => {
        let perm: PermissionStatus = 'unknown';
        if (typeof navigator !== 'undefined' && 'permissions' in navigator) {
          try {
            const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
            perm = status.state as PermissionStatus;
            status.onchange = () => setPermissionState(status.state as PermissionStatus);
          } catch {
            perm = 'unknown';
          }
        }
        setPermissionState(perm);

        const fire = (cb: () => void) => {
          autoRequestTimerRef.current = window.setTimeout(() => {
            // Re-check route on fire — user may have navigated to /auth, /checkout, etc.
            if (typeof window !== 'undefined' && !isAllowedRoute(window.location.pathname)) return;
            cb();
          }, AUTO_REQUEST_DELAY);
        };

        if (perm === 'granted') {
          fire(() => void runFallbackChain({ tryBrowser: true }));
        } else if (perm === 'denied') {
          fire(() => void runFallbackChain({ tryBrowser: false }));
        } else if (!promptDismissed) {
          fire(() => setShouldShowPrompt(true));
        } else {
          fire(() => void runFallbackChain({ tryBrowser: false }));
        }
      };

      void inspect();
    };

    // Initial attempt
    scheduleIfAllowed();

    // Re-evaluate on SPA navigation. Patch history methods because react-router uses
    // pushState/replaceState which don't fire popstate.
    const NAV_EVENT = 'lov:locationchange';
    const emit = () => window.dispatchEvent(new Event(NAV_EVENT));

    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    let patched = false;
    if (!(window.history as unknown as { __lovLocPatched?: boolean }).__lovLocPatched) {
      window.history.pushState = function (...args) {
        const r = origPush.apply(this, args as Parameters<typeof origPush>);
        emit();
        return r;
      };
      window.history.replaceState = function (...args) {
        const r = origReplace.apply(this, args as Parameters<typeof origReplace>);
        emit();
        return r;
      };
      (window.history as unknown as { __lovLocPatched: boolean }).__lovLocPatched = true;
      patched = true;
    }

    const onNav = () => scheduleIfAllowed();
    window.addEventListener('popstate', onNav);
    window.addEventListener(NAV_EVENT, onNav);

    return () => {
      if (autoRequestTimerRef.current) {
        window.clearTimeout(autoRequestTimerRef.current);
      }
      window.removeEventListener('popstate', onNav);
      window.removeEventListener(NAV_EVENT, onNav);
      if (patched) {
        window.history.pushState = origPush;
        window.history.replaceState = origReplace;
        (window.history as unknown as { __lovLocPatched: boolean }).__lovLocPatched = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasDetectedCity = !!(state.city && state.city !== 'your city');

  return (
    <LocationContext.Provider
      value={{
        ...state,
        requestLocation,
        setManualCity,
        dismissPrompt,
        hasDetectedCity,
        shouldShowPrompt,
        needsManualSelection,
        permissionState,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = (): LocationContextType => {
  const ctx = useContext(LocationContext);
  if (!ctx) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return ctx;
};

// Preferred alias to avoid naming collisions with react-router-dom's useLocation.
export const useUserLocation = useLocation;
